/**
 * delete_schedule Tool - Delete a recurring test schedule
 */

import type { Tool } from '@modelcontextprotocol/sdk/types.js';
import type { JobClient } from '../clients/job-client.js';

export interface DeleteScheduleArgs {
  projectId: string;
  scheduleId: string;
}

export const deleteScheduleToolDefinition: Tool = {
  name: 'delete_schedule',
  description: `Delete a recurring test schedule permanently.

This stops all future executions. Past execution history is preserved.
To temporarily stop a schedule without deleting it, use update_schedule with status: "paused" instead.`,
  inputSchema: {
    type: 'object',
    properties: {
      projectId: {
        type: 'string',
        description: 'Project ID (required).',
      },
      scheduleId: {
        type: 'string',
        description: 'Schedule ID to delete (required). Get from list_schedules.',
      },
    },
    required: ['projectId', 'scheduleId'],
  },
};

export async function handleDeleteSchedule(
  args: DeleteScheduleArgs,
  jobClient: JobClient,
): Promise<{ content: Array<{ type: 'text'; text: string }>; isError?: boolean }> {
  try {
    await jobClient.deleteSchedule(args.projectId, args.scheduleId);

    return {
      content: [{
        type: 'text',
        text: `Schedule deleted successfully.

Schedule ID: ${args.scheduleId}

All future executions have been cancelled. Past execution history is preserved.`,
      }],
    };
  } catch (error) {
    const err = error as Error & { status?: number };
    return {
      content: [{
        type: 'text',
        text: `Failed to delete schedule\n\nError: ${err.message}${err.status ? `\nStatus: ${err.status}` : ''}`,
      }],
      isError: true,
    };
  }
}
